import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import MediaQuery from '@ynap/media-query';
import { withOptimizely } from '@ynap/optimizely-utils';
import Portal from '@ynap/portal';
import ProductSummary from '../ProductSummary/ProductSummary';
import RecommendationsOptionsOverlay from './RecommendationsOptionsOverlay/RecommendationsOptionsOverlay';

const bem = new BEMHelper('ProductSummaryWithOverlay');

const ProductSummaryWithOverlay = (props) => {
    const {
        product,
        openProductDetail,
        messages,
        loading,
        className,
        productImageAspectRatio,
        children,
        locale,
        optimizely,
        overlayProductId,
        handleOverlayProductIdChange,
        setIsPhoneOverlay,
        CTAButtonsState,
        addMicrodata,
        isWishlist,
    } = props;

    const { productId, selectedProductVariant } = product;
    const { sKUs } = selectedProductVariant;
    const { addToCartLoading = false, addToWishlistLoading = false } = CTAButtonsState;
    const singleSizeItem = sKUs.length > 1 ? null : { sizeSku: sKUs[0], sizeLabel: sKUs[0]?.size?.labelSize };
    const [selectedSize, setSelectedSize] = useState(singleSizeItem);
    const [noSizeNotification, setNoSizeNotification] = useState(null);

    const selectedProductVariantWithBuyableStatus = {
        ...selectedProductVariant,
        buyable: {
            canAddToBag: true,
            canAddToWishlist: true,
        },
    };

    // Handle closing the overlay if clicking outside of it
    const wrapperRef = useRef(null);
    const phoneWrapperRef = useRef(null);
    const isOverlayOpenForThisProduct = overlayProductId === productId;
    useEffect(() => {
        function handleClickOutside(event) {
            if (addToCartLoading || addToWishlistLoading) {
                return;
            }

            const isClickOutsideOverlay = phoneWrapperRef?.current
                ? !phoneWrapperRef.current?.contains(event.target)
                : wrapperRef?.current && !wrapperRef.current?.contains(event.target);

            if (isClickOutsideOverlay) {
                handleOverlayProductIdChange(event, null);
                setSelectedSize(singleSizeItem);
                setNoSizeNotification(null);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            setNoSizeNotification(null);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [addToCartLoading, addToWishlistLoading, wrapperRef, phoneWrapperRef]);

    const onOverlayProductIdChange = (event, productId) => {
        handleOverlayProductIdChange(event, productId);
    };

    return (
        <div className={bem()}>
            {!isOverlayOpenForThisProduct && (
                <i
                    className={bem('open')}
                    onClick={(event) => {
                        onOverlayProductIdChange(event, productId);
                        /* istanbul ignore next */
                        optimizely.track('wl_recommendationsplus');
                    }}
                />
            )}

            <ProductSummary
                product={product}
                messages={messages}
                onClick={openProductDetail}
                loading={loading}
                className={bem('productSummary', undefined, className)}
                productImageAspectRatio={productImageAspectRatio}
                locale={locale}
                addMicrodata={addMicrodata}
                isWishlist={isWishlist}
            >
                {children}
            </ProductSummary>

            {isOverlayOpenForThisProduct && (
                <MediaQuery
                    screens={{
                        phone: () => {
                            setIsPhoneOverlay(true);
                            return (
                                <Portal show={`phoneOverlay-${productId}`}>
                                    <div key={`phoneOverlay-${productId}`} className={bem('phoneOverlay')}>
                                        <div className={bem('phoneOverlayMask')}></div>
                                        <div className={bem('phoneOverlayOptions')} ref={phoneWrapperRef}>
                                            <i
                                                className={bem('close')}
                                                onClick={(event) => {
                                                    handleOverlayProductIdChange(event, null);
                                                }}
                                            />
                                            <ProductSummary
                                                product={product}
                                                messages={messages}
                                                onClick={openProductDetail}
                                                loading={loading}
                                                className={bem('productSummary', undefined, className)}
                                                productImageAspectRatio={productImageAspectRatio}
                                                locale={locale}
                                                addMicrodata={addMicrodata}
                                            >
                                                {children}
                                            </ProductSummary>
                                            <RecommendationsOptionsOverlay
                                                product={product}
                                                messages={messages}
                                                loading={loading}
                                                optimizely={optimizely}
                                                wrapperRef={wrapperRef}
                                                selectedProductVariantWithBuyableStatus={selectedProductVariantWithBuyableStatus}
                                                selectedSize={selectedSize}
                                                setSelectedSize={setSelectedSize}
                                                noSizeNotification={noSizeNotification}
                                                setNoSizeNotification={setNoSizeNotification}
                                                CTAButtonsState={CTAButtonsState}
                                            />
                                        </div>
                                    </div>
                                </Portal>
                            );
                        },
                        tablet: () => {
                            setIsPhoneOverlay(false);
                            return (
                                <RecommendationsOptionsOverlay
                                    product={product}
                                    messages={messages}
                                    loading={loading}
                                    optimizely={optimizely}
                                    wrapperRef={wrapperRef}
                                    selectedProductVariantWithBuyableStatus={selectedProductVariantWithBuyableStatus}
                                    selectedSize={selectedSize}
                                    setSelectedSize={setSelectedSize}
                                    noSizeNotification={noSizeNotification}
                                    setNoSizeNotification={setNoSizeNotification}
                                    CTAButtonsState={CTAButtonsState}
                                />
                            );
                        },
                        desktop: () => {
                            setIsPhoneOverlay(false);
                            return (
                                <RecommendationsOptionsOverlay
                                    product={product}
                                    messages={messages}
                                    loading={loading}
                                    optimizely={optimizely}
                                    wrapperRef={wrapperRef}
                                    selectedProductVariantWithBuyableStatus={selectedProductVariantWithBuyableStatus}
                                    selectedSize={selectedSize}
                                    setSelectedSize={setSelectedSize}
                                    noSizeNotification={noSizeNotification}
                                    setNoSizeNotification={setNoSizeNotification}
                                    CTAButtonsState={CTAButtonsState}
                                />
                            );
                        },
                    }}
                />
            )}
        </div>
    );
};

ProductSummaryWithOverlay.propTypes = {
    product: PropTypes.object.isRequired,
    openProductDetail: PropTypes.func,
    messages: PropTypes.shape({
        product: PropTypes.shape({
            viewMoreDetails: PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,
    modifier: PropTypes.string,
    loading: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
    productImageAspectRatio: PropTypes.string,
    locale: PropTypes.string.isRequired,
    optimizely: PropTypes.shape({
        track: PropTypes.func.isRequired,
    }),
    overlayProductId: PropTypes.string,
    handleOverlayProductIdChange: PropTypes.func,
    setIsPhoneOverlay: PropTypes.func,
    CTAButtonsState: PropTypes.shape({
        addToCart: PropTypes.func,
        addToCartLoading: PropTypes.bool,
        wishlists: PropTypes.array,
        wishlistId: PropTypes.string,
        addToWishlist: PropTypes.func,
        addToWishlistLoading: PropTypes.bool,
        errors: PropTypes.object,
    }),
    addMicrodata: PropTypes.bool,
    isWishlist: PropTypes.bool,
};

export default withOptimizely(ProductSummaryWithOverlay);
